import React, { useState, useEffect, useRef } from 'react';
import { Code, Server, Layout, Cloud, ClipboardList, Briefcase, Palette, ShoppingCart, Database, CreditCard, Plus } from 'lucide-react';

interface Role {
  icon: React.ReactNode;
  title: string;
}

const roles: Role[] = [
  { icon: <Code className="w-4 h-4" />, title: "Software Engineers" },
  { icon: <Cloud className="w-4 h-4" />, title: "DevOps Engineers" },
  { icon: <Database className="w-4 h-4" />, title: "Data Analysts & Scientists" },
  { icon: <ClipboardList className="w-4 h-4" />, title: "Product & Project Managers" },
  { icon: <ShoppingCart className="w-4 h-4" />, title: "Sales & Marketing Executives" },
  { icon: <Palette className="w-4 h-4" />, title: "Graphic Designers" },
  { icon: <Palette className="w-4 h-4" />, title: "UX/UI Designers" },
  { icon: <CreditCard className="w-4 h-4" />, title: "Accountants & Auditors" },
  { icon: <Plus className="w-4 h-4" />, title: "..Many More!" },
];

export function RoleChips() {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [visibleRoles, setVisibleRoles] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Staggered animation sequence
          setTimeout(() => setAnimationPhase(1), 100); // Heading
          
          // Reveal roles with staggered timing - faster than before
          setTimeout(() => {
            roles.forEach((_, index) => {
              setTimeout(() => {
                setVisibleRoles(prev => [...prev, index]);
              }, index * 80); // Reduced from 100ms to 80ms for quicker sequence
            });
          }, 300); // Slightly reduced delay before starting role animations
          
          observer.disconnect();
        }
      },
      { threshold: 0.2 } // Trigger when 20% of section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div 
      ref={sectionRef}
      className="relative mt-24 py-10"
    >
      {/* Background Gradients with animation */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 opacity-0 transition-opacity duration-1000" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)] opacity-0 transition-opacity duration-1000 delay-150" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(16,185,129,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_50%)] opacity-0 transition-opacity duration-1000 delay-300" 
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      
      {/* Content */}
      <div className="relative container mx-auto px-4">
        <h3 
          className={`text-center text-2xl md:text-3xl font-medium bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark mb-12 transform transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Job-Ready Interview Practice & Drills for
        </h3>
        
        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
          {roles.map((role, index) => (
            <div
              key={index}
              className="group relative flex items-center gap-3 px-6 py-3 rounded-2xl
                       bg-gradient-to-br from-background-light/80 to-surface-light/80
                       dark:from-background-dark/80 dark:to-surface-dark/80
                       backdrop-blur-md
                       border border-border-light dark:border-border-dark
                       transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                       hover:border-primary-light/40 dark:hover:border-primary-dark/40
                       hover:-translate-y-1
                       overflow-hidden"
              style={{
                opacity: visibleRoles.includes(index) ? 1 : 0,
                transform: visibleRoles.includes(index) 
                  ? 'translateY(0)' 
                  : 'translateY(20px)',
                transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${index * 0.05}s`
              }}
            >
              {/* Subtle shine effect */}
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 -translate-x-full group-hover:translate-x-full group-hover:opacity-10 transition-all duration-1000 ease-out pointer-events-none"></span>
              
              {/* Background glow on hover */}
              <span className="absolute inset-0 bg-gradient-to-r from-primary-light/5 to-secondary-light/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none"></span>
              
              <div className="relative z-10 flex items-center gap-3">
                <span className="p-2 rounded-xl bg-primary-light/10 dark:bg-primary-dark/10
                               text-primary-light dark:text-primary-dark
                               transition-all duration-300 group-hover:bg-primary-light/15 dark:group-hover:bg-primary-dark/15">
                  {role.icon}
                </span>
                <span className="text-sm font-medium whitespace-nowrap group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors duration-300">
                  {role.title}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}